/**
 * Dashboard view after organization is created: step cards and Go to Dashboard FAB.
 * Single Responsibility: render organization name (with Edit when primary admin), step cards grid, and onboarding FAB.
 * Open/Closed: step configs and step data come from props/constants; navigation from callback.
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Pencil } from 'lucide-react';
import { OnboardingFAB } from '../../components/organizationWizard/OnBoardingFAB';
import { StepCard } from './components/StepCard';
import { STEP_CONFIGS } from './constants';
import { getStepData } from './utils/stepData';

const ORGANIZATION_HEADING_CLASS =
  'font-[\'Roboto:Bold\',sans-serif] font-bold text-[32px] md:text-[40px] text-black tracking-[0.16px]';
const ORGANIZATION_HEADING_STYLE = { fontVariationSettings: "'wdth' 100" };
const GRID_CLASS =
  'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8';

function OrganizationHeader({
  organizationName,
  isPrimaryAdmin,
  canEditOrganization,
  onEditOrganization,
}) {
  const showEdit = isPrimaryAdmin && canEditOrganization && onEditOrganization;
  return (
    <div className="mb-8 md:mb-12 flex items-center gap-3 flex-wrap">
      <h1 className={ORGANIZATION_HEADING_CLASS} style={ORGANIZATION_HEADING_STYLE}>
        {organizationName}
      </h1>
      {showEdit && (
        <button
          type="button"
          onClick={onEditOrganization}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#61b8e7] text-white font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] hover:bg-[#4da3d1] transition-colors"
          aria-label="Edit organization"
        >
          <Pencil className="size-4" />
          Edit
        </button>
      )}
    </div>
  );
}

OrganizationHeader.propTypes = {
  organizationName: PropTypes.string.isRequired,
  isPrimaryAdmin: PropTypes.bool,
  canEditOrganization: PropTypes.bool,
  onEditOrganization: PropTypes.func,
};

function DashboardOrganizationCreatedComponent({
  organizationName = 'Jan-Way',
  isPrimaryAdmin = false,
  canEditOrganization = false,
  onEditOrganization,
  wizardStatus,
  onStepClick,
  stepConfigs = STEP_CONFIGS,
  step1Completed = false,
  step3Completed = false,
  canGoToDashboard,
  onGoToDashboard,
}) {
  const handleStepClick = useCallback(
    (stepNumber) => {
      onStepClick?.(stepNumber);
    },
    [onStepClick],
  );

  return (
    <div className="w-full" data-name="Dashboard - Organization created">
      <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-6 lg:px-8">
        <OrganizationHeader
          organizationName={organizationName}
          isPrimaryAdmin={isPrimaryAdmin}
          onEditOrganization={onEditOrganization}
        />

        <div className={GRID_CLASS}>
          {stepConfigs.map((config) => {
            const stepData = getStepData(wizardStatus, config.stepNumber);
            const { IconComponent } = config;

            return (
              <StepCard
                key={config.stepNumber}
                isEnabled={stepData.isEnabled}
                stepNumber={config.stepNumber}
                title={config.title}
                copy={config.copy}
                buttonLabel={config.defaultButtonLabel}
                iconComponent={<IconComponent isEnabled={stepData.isEnabled} />}
                onClick={handleStepClick}
                stats={stepData.stats}
                getDynamicLabel={config.getButtonLabel}
              />
            );
          })}
        </div>
      </div>

      {onGoToDashboard && (
        <OnboardingFAB
          step1Completed={step1Completed}
          step3Completed={step3Completed}
          canGoToDashboard={canGoToDashboard}
          onClick={onGoToDashboard}
        />
      )}
    </div>
  );
}

DashboardOrganizationCreatedComponent.propTypes = {
  organizationName: PropTypes.string,
  isPrimaryAdmin: PropTypes.bool,
  canEditOrganization: PropTypes.bool,
  onEditOrganization: PropTypes.func,
  wizardStatus: PropTypes.shape({
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        step: PropTypes.number.isRequired,
        isEnabled: PropTypes.bool.isRequired,
        isCompleted: PropTypes.bool,
        stats: PropTypes.shape({
          facilitiesCount: PropTypes.number,
          usersCount: PropTypes.number,
          standardsConfigured: PropTypes.number,
        }),
      }),
    ),
  }),
  onStepClick: PropTypes.func,
  stepConfigs: PropTypes.arrayOf(PropTypes.object),
  step1Completed: PropTypes.bool,
  step3Completed: PropTypes.bool,
  canGoToDashboard: PropTypes.bool,
  onGoToDashboard: PropTypes.func,
};

const DashboardOrganizationCreated = React.memo(DashboardOrganizationCreatedComponent);
export default DashboardOrganizationCreated;
